import { reactive } from 'vue'

import axios from 'axios'
import * as _ from 'lodash-es'
import moment from 'moment'

import alertService from '../../common/service/alertService.js'
import loginService from '../../login/service/loginService.js'
import roleService from '../../login/service/roleService.js'
import sequenceService from '../../common/service/sequenceService.js'
import jobPrefix from '../../common/service/jobPrefix.js'
import userStore from '../../common/service/userStore.js'
import jobStatus from '../../common/jobStatus.js'

/*
 * The job window state is `Vue.observable` (step 41 of #614). The Vue components revealed the
 * window through `services.watch` - a `$rootScope.$watch` - and the callers opened it from outside
 * a digest, so it needed an explicit `notifyAngular()` to be seen. Vue reactivity removes both.
 */
const state = reactive({ jobWindowIsOpen: false, currentJob: undefined })

let savedJob

var jobService = {
  addJob: async options => {
    try {
      const sequenceResult = await sequenceService.next('job')

      savedJob = _.cloneDeep(state.currentJob)

      var newJob = {}
      newJob.jobNumber = sequenceResult.data.nextItem.toString()
      // The session check that sets userStore.getUser() is async, and Add Job can be clicked before
      // it lands. Reading `user.fullname` unguarded threw, the catch below swallowed it, and the
      // window never opened - the intermittent `#jobTitle`-hidden smoke failure. Leave the
      // producer blank in that case; the form requires one before it can be saved.
      newJob.producer = userStore.getUser() ? userStore.getUser().fullname : undefined

      if (roleService.hasRole('admin')) {
        newJob.status = jobStatus.QUOTING
      } else {
        newJob.status = jobStatus.IN_PROGRESS
      }
      state.currentJob = newJob

      if (options.openWindow) {
        jobService.openJobWindow()
      }

      return newJob
    } catch (err) {
      console.log(err.stack)
    }
  },

  restoreSavedJob: function () {
    state.currentJob = savedJob
  },

  getAllJobsForDate: function (selectedDate, view) {
    return axios
      .get('/api/job/date/' + view + '/' + moment(selectedDate).format('YYYY-MM-DD'))
      .then(function (httpResult) {
        return httpResult.data
      })
  },

  setJobByBooking: function (booking) {
    return axios.get('/api/job/booking/' + booking.sequence).then(function (httpResult) {
      if (httpResult.data.job) {
        const job = httpResult.data.job
        jobService.setCurrentJob(job)
        return job
      }
    })
  },

  setCurrentJob: function (job) {
    // No date coercion here: a right's dates are Dates because the form's date inputs convert both
    // ways (#460), and `normalizeRight` coerces anything saved before that. This used to convert
    // strings that the form itself had written, and blamed Mongo.
    if (job) {
      state.currentJob = job
      savedJob = _.cloneDeep(state.currentJob)
      // `angular-local-storage` prefixes every key with `ls.`, and store/job reads that exact key.
      localStorage.setItem('ls.currentJob', JSON.stringify({ id: state.currentJob._id }))
    }
  },

  getTotalHours: function () {
    var totalHours = 0
    if (state.currentJob) {
      _.each(state.currentJob.bookings, function (booking) {
        totalHours += booking.hours
      })
    }
    return totalHours
  },

  getCurrentJob: function () {
    return state.currentJob
  },

  clearCurrentJob: function () {
    state.currentJob = undefined
  },

  openJobWindow: function () {
    state.jobWindowIsOpen = true
  },

  getJobWindowOpen: function () {
    return state.jobWindowIsOpen
  },

  closeJobWindow: function () {
    state.jobWindowIsOpen = false
  },

  findById: function (id) {
    return axios.get('/api/job/id/' + id).then(function (httpResult) {
      return httpResult.data.job
    })
  },

  save: async ({ bookingToSave = undefined, keepJobWindowOpen = false } = {}) => {
    return axios
      .post('/api/job', { job: state.currentJob, booking: bookingToSave, username: userStore.getUser().name })
      .then(httpResult => {
        if (httpResult.data.job) {
          jobService.setCurrentJob(httpResult.data.job)
          state.jobWindowIsOpen = keepJobWindowOpen
          alertService.add('success', jobPrefix.get() + state.currentJob.jobNumber + ' saved')
        }
        return httpResult.data.job
      })
      .catch(error => {
        console.log(error)
        // The backend refuses a save built on a stale read with a 409 and a sentence saying what to
        // do (#679). Show that rather than "there was a problem", which says nothing actionable.
        const refused = error.response && error.response.data && error.response.data.error
        alertService.add('error', refused || 'There was a problem saving the job')
      })
      .finally(function () {
        if (!keepJobWindowOpen) {
          jobService.closeJobWindow()
        }
      })
  },

  getExistingBrands: function (brandQuery) {
    return axios
      .get('/api/job/existingBrands', {
        params: {
          brand: brandQuery,
        },
      })
      .then(function (httpResult) {
        return httpResult.data.brands
      })
  },

  refreshCurrentJob: function () {
    // this seems to be the easiest way to return a promise and deal with not having a job set yet
    var currentJobId = state.currentJob ? state.currentJob._id : undefined

    return jobService.findById(currentJobId).then(function (job) {
      if (job) {
        jobService.setCurrentJob(job)
      }
    })
  },

  refreshAllJobs: function () {
    return axios.get('/api/job/refresh')
  },

  findIncompleteBookings: function (resources) {
    let resourceIds = _.map(resources, '_id')

    return axios({
      method: 'GET',
      url: '/api/job/incomplete',
      params: {
        resources: resourceIds,
      },
    }).then(resources => {
      return resources
    })
  },

  getProducers: async () => {
    const userList = await loginService.userList('producer')
    return userList.Users.map(user => {
      const userName = _.find(user.Attributes, {
        Name: 'name',
      })
        ? _.find(user.Attributes, { Name: 'name' }).Value
        : ''
      return { userId: user.Username, name: userName }
    })
  },
}

export default jobService
